






















import { useVModel } from '@vueuse/core';
import {
  computed,
  defineComponent,
  PropType,
  ref,
  onMounted
} from '@vue/composition-api';

import { useFetchEmailActionEmailRecipients } from '@/composable/useWorkflowTransitionAction';

import {
  IUser,
  IWorkflowTransitionActionEmailRecipient,
  IWorkflowTransitionActionEmailRecipientData,
  TransitionActionRecipientSubTypeEnum,
  TransitionActionRecipientTypeEnum,
  WorkflowTransitionActionEmailRecipient
} from '@/models';
import { VCombobox } from 'vuetify/lib/components';

interface RecipientComboBoxOptions extends InstanceType<typeof VCombobox> {
  getMenuIndex: () => number;
  updateEditing: () => void;
  getText: (item: any) => string;
  setValue: (item: any) => void;
  selectItem: (item: any) => void;
  updateTags: () => void;
  internalValue: any | any[];
  searchIsDirty: boolean;
  internalSearch: null | string;
  editingIndex: number;
  selectedItems: [];
}
const RecipientComboBox = VCombobox.extend<RecipientComboBoxOptions>({
  functional: false
}).extend({
  name: 'RecipientComboBox',

  props: {
    subtype: {
      type: Number
    }
  },

  methods: {
    /**
     * Updating current implementation from vuetify VComboBox to allow
     * saving an object of IWorkflowTransitionActionEmailRecipientData instead of the string provided
     */
    updateTags() {
      const menuIndex = this.getMenuIndex();

      // If the user is not searching
      // and no menu item is selected
      // or if the search is empty
      // do nothing
      if ((menuIndex < 0 && !this.searchIsDirty) || !this.internalSearch)
        return;

      if (this.editingIndex > -1) {
        return this.updateEditing();
      }

      const index = this.selectedItems.findIndex(
        item => this.internalSearch === this.getText(item)
      );

      // If the duplicate item is an object,
      // copy it, so that it can be added again later
      const itemToSelect =
        index > -1 && typeof this.selectedItems[index] === 'object'
          ? Object.assign({}, this.selectedItems[index])
          : ({
              email: this.internalSearch,
              recipient_type: TransitionActionRecipientTypeEnum.Email,
              recipient_subtype: this.subtype
            } as IWorkflowTransitionActionEmailRecipientData);

      // If it already exists, do nothing
      // this might need to change to bring
      // the duplicated item to the last entered
      if (index > -1) {
        const internalValue = this.internalValue.slice();
        internalValue.splice(index, 1);

        this.setValue(internalValue);
      }

      // If menu index is greater than 1
      // the selection is handled elsewhere
      if (menuIndex > -1) return (this.internalSearch = null);

      this.selectItem(itemToSelect);

      this.internalSearch = null;
    }
  }
});
export default defineComponent({
  name: 'WorkflowTransitionActionEmailRecipientsAutoCompletePicker',

  components: {
    RecipientComboBox
  },

  props: {
    value: [Array, Number, Object] as PropType<
      | IWorkflowTransitionActionEmailRecipient
      | number
      | (IWorkflowTransitionActionEmailRecipient | number)[]
    >,

    errorMessages: {
      type: [String, Array] as PropType<string | string[]>
    },

    subtype: {
      type: Number,
      default: TransitionActionRecipientSubTypeEnum.Direct
    },

    workflowId: {
      type: Number,
      required: true
    },

    transitionId: {
      type: Number,
      required: true
    }
  },

  setup(props) {
    const recipientsVModel = useVModel(props, 'value', (_, newValue) => {
      if (Array.isArray(newValue)) {
        const recipientsList = newValue
          .map(value => {
            return typeof value === 'string'
              ? {
                  email: value,
                  recipient_type: TransitionActionRecipientTypeEnum.Email,
                  recipient_subtype: props.subtype
                }
              : {
                  id: value.id,
                  email: value.email,
                  recipient_type: value.recipient_type,
                  recipient_subtype: props.subtype
                };
          })
          .filter(
            recipient =>
              recipient.recipient_type ===
              TransitionActionRecipientTypeEnum.Email
          );

        return recipientsList;
      }
      return newValue;
    });

    const errorMessageList = computed(() => {
      let errorList: string[] = [];

      if (recipientsError.value) {
        errorList.push(recipientsError.value.message);
      }
      if (props.errorMessages) {
        if (Array.isArray(props.errorMessages)) {
          errorList.push(...props.errorMessages);
        } else {
          errorList.push(props.errorMessages);
        }
      }

      return errorList;
    });

    const {
      recipients,
      isLoadingRecipients,
      recipientsError,
      refetchRecipients
    } = useFetchEmailActionEmailRecipients(
      props.workflowId,
      props.transitionId
    );

    onMounted(() => {
      refetchRecipients();
    });

    const emailRecipients = computed(() =>
      recipients.value.map(
        (recipient: IUser) =>
          new WorkflowTransitionActionEmailRecipient({
            email: recipient.email,
            recipient_type: TransitionActionRecipientTypeEnum.Email,
            recipient_subtype: props.subtype
          })
      )
    );

    // /** @public */
    const comboboxRef = ref<
      InstanceType<typeof VCombobox> & { reset: () => void }
    >();

    /** @public */
    function reset() {
      if (comboboxRef.value) comboboxRef.value.reset();
    }

    function getRecipientValue(
      recipient: IWorkflowTransitionActionEmailRecipient
    ) {
      return {
        id: recipient.id,
        email: recipient.email,
        recipient_type: recipient.recipient_type,
        recipient_subtype: props.subtype
      };
    }

    function getRecipientText(
      recipient: IWorkflowTransitionActionEmailRecipient
    ) {
      return recipient.email;
    }

    return {
      reset,
      emailRecipients,
      getRecipientValue,
      getRecipientText,
      comboboxRef,
      recipientsVModel,
      isLoadingRecipients,
      errorMessageList
    };
  }
});
